/**
 * Authentication UI
 * Login/Signup modals and auth header management
 */

import { authService } from './auth-service.js';
import { isConfigured } from '../config/config.js';
import { trackSignupCompleted, trackLoginCompleted, identifyUser } from '../app/analytics/event-tracker.js';
import { showNotification } from '../lib/notification-utils.js';

class AuthUI {
  constructor() {
    this.modal = null;
    this.currentView = 'login'; // 'login', 'signup', 'forgot'
    this.onAuthSuccess = null;
    this.onUpgradeClick = null; // Callback for upgrade button
    this.onHistoryClick = null; // Callback for transaction history
    this.onAccountClick = null; // Callback for account settings
  }

  /**
   * Initialize Auth UI - create modal and header elements
   */
  init() {
    this.createAuthModal();
    this.createAuthHeader();
    this.attachEventListeners();

    // Listen for auth state changes
    authService.onAuthStateChange((event, session) => {
      this.updateAuthHeader();
      if (event === 'SIGNED_IN' && this.onAuthSuccess) {
        this.onAuthSuccess(session.user);
        this.hideModal();
      } else if (event === 'PASSWORD_RECOVERY') {
        // User clicked password reset link from email - show reset form
        this.showPasswordResetView();
      }
    });

    // Initial header update
    this.updateAuthHeader();

    // Check if we arrived via password recovery link
    // The PASSWORD_RECOVERY event fires during authService.init() before this listener is registered,
    // so we need to check the URL hash for recovery tokens
    this.checkForRecoveryToken();
  }

  /**
   * Check for pending password recovery
   * Called on init to handle password recovery flow
   */
  checkForRecoveryToken() {
    // Check if authService detected password recovery mode
    if (authService.pendingPasswordRecovery) {
      console.log('[AuthUI] Password recovery mode - showing password reset form');
      authService.pendingPasswordRecovery = false; // Clear the flag
      this.showPasswordResetView();
      return;
    }

    // Also check URL hash as a fallback (for non-PKCE flows)
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery')) {
      console.log('[AuthUI] Password recovery mode (hash) - showing password reset form');
      this.showPasswordResetView();
    }
  }

  /**
   * Create the auth modal HTML
   */
  createAuthModal() {
    const modalHTML = `
      <div id="authModal" class="auth-modal" style="display: none;">
        <div class="auth-modal-backdrop"></div>
        <div class="auth-modal-content">
          <button class="auth-modal-close" id="authModalClose">&times;</button>

          <!-- Login View -->
          <div id="authLoginView" class="auth-view">
            <h2>Welcome Back</h2>
            <p class="auth-subtitle">Sign in to access your account</p>

            <form id="loginForm" class="auth-form">
              <div class="auth-form-group">
                <label for="loginEmail">Email</label>
                <input type="email" id="loginEmail" required placeholder="your@email.com">
              </div>
              <div class="auth-form-group">
                <label for="loginPassword">Password</label>
                <input type="password" id="loginPassword" required placeholder="Your password">
              </div>
              <div class="auth-error" id="loginError"></div>
              <button type="submit" class="btn btn-primary auth-submit">Sign In</button>
            </form>

            <div class="auth-divider">
              <span>or</span>
            </div>

            <button id="googleSignInBtn" class="btn auth-oauth-btn">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div class="auth-links">
              <a href="#" id="forgotPasswordLink">Forgot password?</a>
              <span class="auth-link-separator">|</span>
              <a href="#" id="showSignupLink">Create account</a>
            </div>
          </div>

          <!-- Signup View -->
          <div id="authSignupView" class="auth-view" style="display: none;">
            <h2>Create Account</h2>
            <p class="auth-subtitle">Sign up to unlock all features</p>

            <form id="signupForm" class="auth-form">
              <div class="auth-form-group">
                <label for="signupEmail">Email</label>
                <input type="email" id="signupEmail" required placeholder="your@email.com">
              </div>
              <div class="auth-form-group">
                <label for="signupPassword">Password</label>
                <input type="password" id="signupPassword" required minlength="6" placeholder="At least 6 characters">
              </div>
              <div class="auth-form-group">
                <label for="signupPasswordConfirm">Confirm Password</label>
                <input type="password" id="signupPasswordConfirm" required placeholder="Confirm your password">
              </div>
              <div class="auth-error" id="signupError"></div>
              <button type="submit" class="btn btn-primary auth-submit">Create Account</button>
            </form>

            <div class="auth-divider">
              <span>or</span>
            </div>

            <button id="googleSignUpBtn" class="btn auth-oauth-btn">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div class="auth-links">
              <span>Already have an account?</span>
              <a href="#" id="showLoginLink">Sign in</a>
            </div>
          </div>

          <!-- Forgot Password View -->
          <div id="authForgotView" class="auth-view" style="display: none;">
            <h2>Reset Password</h2>
            <p class="auth-subtitle">Enter your email to receive a reset link</p>

            <form id="forgotForm" class="auth-form">
              <div class="auth-form-group">
                <label for="forgotEmail">Email</label>
                <input type="email" id="forgotEmail" required placeholder="your@email.com">
              </div>
              <div class="auth-error" id="forgotError"></div>
              <div class="auth-success" id="forgotSuccess" style="display: none;"></div>
              <button type="submit" class="btn btn-primary auth-submit">Send Reset Link</button>
            </form>

            <div class="auth-links">
              <a href="#" id="backToLoginLink">Back to sign in</a>
            </div>
          </div>

          <!-- Password Reset View (shown after clicking email link) -->
          <div id="authResetPasswordView" class="auth-view" style="display: none;">
            <h2>Set New Password</h2>
            <p class="auth-subtitle">Enter your new password below</p>

            <form id="resetPasswordForm" class="auth-form">
              <div class="auth-form-group">
                <label for="resetNewPassword">New Password</label>
                <input type="password" id="resetNewPassword" required minlength="6"
                       placeholder="At least 6 characters">
              </div>
              <div class="auth-form-group">
                <label for="resetConfirmPassword">Confirm Password</label>
                <input type="password" id="resetConfirmPassword" required
                       placeholder="Confirm your password">
              </div>
              <div class="auth-error" id="resetPasswordError"></div>
              <div class="auth-success" id="resetPasswordSuccess" style="display: none;"></div>
              <button type="submit" class="btn btn-primary auth-submit">Update Password</button>
            </form>

            <div class="auth-links">
              <a href="#" id="resetPasswordCancelLink">Cancel</a>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('authModal');
  }

  /**
   * Create the auth header HTML
   */
  createAuthHeader() {
    const header = document.querySelector('header');
    if (!header) return;

    const authHeaderHTML = `
      <div id="authHeader" class="auth-header">
        <!-- Logged out state -->
        <div id="authLoggedOut" class="auth-header-buttons">
          <button id="headerLoginBtn" class="btn btn-small">Log In</button>
          <button id="headerSignupBtn" class="btn btn-small btn-primary">Sign Up</button>
        </div>

        <!-- Logged in state -->
        <div id="authLoggedIn" class="auth-header-user" style="display: none;">
          <span id="headerTierBadge" class="tier-badge">FREE</span>
          <span id="headerCredits" class="credits-badge" style="display: none;">
            <span class="credits-icon">&#9733;</span>
            <span id="headerCreditsBalance">0</span>
          </span>
          <div class="auth-user-menu">
            <button id="headerUserBtn" class="btn btn-small auth-user-btn">
              <span id="headerUserEmail">user@email.com</span>
              <span class="auth-user-arrow">&#9662;</span>
            </button>
            <div id="headerUserDropdown" class="auth-user-dropdown" style="display: none;">
              <a href="#" id="headerAccountLink">Account Settings</a>
              <a href="#" id="headerUpgradeLink">Upgrade Plan</a>
              <a href="#" id="headerHistoryLink">Credit History</a>
              <hr>
              <a href="#" id="headerLogoutLink">Log Out</a>
            </div>
          </div>
        </div>
      </div>
    `;

    header.insertAdjacentHTML('beforeend', authHeaderHTML);
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // Modal close
    document.getElementById('authModalClose')?.addEventListener('click', () => this.hideModal());
    document.querySelector('.auth-modal-backdrop')?.addEventListener('click', () => this.hideModal());

    // View switching
    document.getElementById('showSignupLink')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.showView('signup');
    });
    document.getElementById('showLoginLink')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.showView('login');
    });
    document.getElementById('forgotPasswordLink')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.showView('forgot');
    });
    document.getElementById('backToLoginLink')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.showView('login');
    });

    // Header buttons
    document.getElementById('headerLoginBtn')?.addEventListener('click', () => this.showModal('login'));
    document.getElementById('headerSignupBtn')?.addEventListener('click', () => this.showModal('signup'));

    // User dropdown
    document.getElementById('headerUserBtn')?.addEventListener('click', () => {
      const dropdown = document.getElementById('headerUserDropdown');
      dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      const dropdown = document.getElementById('headerUserDropdown');
      const userBtn = document.getElementById('headerUserBtn');
      if (dropdown && userBtn && !userBtn.contains(e.target) && !dropdown.contains(e.target)) {
        dropdown.style.display = 'none';
      }
    });

    // Logout
    document.getElementById('headerLogoutLink')?.addEventListener('click', async (e) => {
      e.preventDefault();
      try {
        await authService.signOut();
        window.location.reload(); // Refresh to reset app state
      } catch (error) {
        console.error('Logout error:', error);
      }
    });

    // Upgrade link
    document.getElementById('headerUpgradeLink')?.addEventListener('click', (e) => {
      e.preventDefault();
      document.getElementById('headerUserDropdown').style.display = 'none';
      if (this.onUpgradeClick) {
        this.onUpgradeClick();
      }
    });

    // Credit History link
    document.getElementById('headerHistoryLink')?.addEventListener('click', (e) => {
      e.preventDefault();
      document.getElementById('headerUserDropdown').style.display = 'none';
      if (this.onHistoryClick) {
        this.onHistoryClick();
      }
    });

    // Account Settings link
    document.getElementById('headerAccountLink')?.addEventListener('click', (e) => {
      e.preventDefault();
      document.getElementById('headerUserDropdown').style.display = 'none';
      if (this.onAccountClick) {
        this.onAccountClick();
      }
    });

    // Form submissions
    document.getElementById('loginForm')?.addEventListener('submit', (e) => this.handleLogin(e));
    document.getElementById('signupForm')?.addEventListener('submit', (e) => this.handleSignup(e));
    document.getElementById('forgotForm')?.addEventListener('submit', (e) => this.handleForgotPassword(e));

    // OAuth buttons
    document.getElementById('googleSignInBtn')?.addEventListener('click', () => this.handleOAuthSignIn('google'));
    document.getElementById('googleSignUpBtn')?.addEventListener('click', () => this.handleOAuthSignIn('google'));

    // Escape key to close modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal?.style.display !== 'none') {
        this.hideModal();
      }
    });

    // Password reset form (after clicking email link)
    document.getElementById('resetPasswordForm')?.addEventListener('submit', (e) => this.handleResetPassword(e));
    document.getElementById('resetPasswordCancelLink')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.hideModal();
      // Clear recovery token from URL
      if (window.location.hash) {
        history.replaceState(null, '', window.location.pathname);
      }
    });
  }

  /**
   * Show the auth modal
   */
  showModal(view = 'login') {
    if (!isConfigured()) {
      alert('Authentication is not configured. Please set up Supabase credentials in config/config.js');
      return;
    }
    this.showView(view);
    this.modal.style.display = 'flex';
    this.clearErrors();
  }

  /**
   * Hide the auth modal
   */
  hideModal() {
    this.modal.style.display = 'none';
    this.clearErrors();
    this.clearForms();
  }

  /**
   * Show password reset view (called when user clicks reset link from email)
   */
  showPasswordResetView() {
    this.showView('resetPassword');
    this.modal.style.display = 'flex';
    this.clearErrors();
  }

  /**
   * Show a specific view in the modal
   */
  showView(view) {
    this.currentView = view;
    document.getElementById('authLoginView').style.display = view === 'login' ? 'block' : 'none';
    document.getElementById('authSignupView').style.display = view === 'signup' ? 'block' : 'none';
    document.getElementById('authForgotView').style.display = view === 'forgot' ? 'block' : 'none';
    document.getElementById('authResetPasswordView').style.display = view === 'resetPassword' ? 'block' : 'none';
  }

  /**
   * Clear all error messages
   */
  clearErrors() {
    document.querySelectorAll('.auth-error').forEach(el => el.textContent = '');
    const forgotSuccess = document.getElementById('forgotSuccess');
    if (forgotSuccess) forgotSuccess.style.display = 'none';
  }

  /**
   * Clear all form inputs
   */
  clearForms() {
    document.querySelectorAll('.auth-form input').forEach(input => input.value = '');
  }

  /**
   * Show error message
   */
  showError(elementId, message) {
    const errorEl = document.getElementById(elementId);
    if (errorEl) {
      errorEl.textContent = message;
    }
  }

  /**
   * Handle login form submission
   */
  async handleLogin(e) {
    e.preventDefault();
    this.clearErrors();

    const email = document.getElementById('loginEmail').value.trim();
    const password = document.getElementById('loginPassword').value;

    try {
      const submitBtn = e.target.querySelector('.auth-submit');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Signing in...';

      const result = await authService.signIn(email, password);
      trackLoginCompleted('email');
      if (result?.user) {
        identifyUser(result.user.id, { email: result.user.email });
      }
      this.hideModal();
    } catch (error) {
      this.showError('loginError', error.message || 'Sign in failed. Please check your credentials.');
    } finally {
      const submitBtn = e.target.querySelector('.auth-submit');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Sign In';
    }
  }

  /**
   * Handle signup form submission
   * If user is currently anonymous, links their identity to preserve data
   */
  async handleSignup(e) {
    e.preventDefault();
    this.clearErrors();

    const email = document.getElementById('signupEmail').value.trim();
    const password = document.getElementById('signupPassword').value;
    const passwordConfirm = document.getElementById('signupPasswordConfirm').value;

    if (password !== passwordConfirm) {
      this.showError('signupError', 'Passwords do not match');
      return;
    }

    if (password.length < 6) {
      this.showError('signupError', 'Password must be at least 6 characters');
      return;
    }

    try {
      const submitBtn = e.target.querySelector('.auth-submit');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Creating account...';

      // Check if user is currently anonymous - link identity instead of new signup
      // This preserves all usage history and data under the same user ID
      if (authService.isAnonymous()) {
        const result = await authService.linkEmailIdentity(email, password);
        // Identity linked successfully - user keeps same ID with new credentials
        trackSignupCompleted('email');
        if (result?.user) {
          identifyUser(result.user.id, { email: result.user.email });
        }
        if (result?.upgradeError) {
          console.warn('[AuthUI] Account linked but tier upgrade failed — will retry on next entitlements fetch');
          showNotification(
            'Your account was created, but plan upgrade is still syncing. Please refresh shortly.',
            { type: 'warning' }
          );
        }
        this.hideModal();
      } else {
        const result = await authService.signUp(email, password);

        if (result.user && !result.session) {
          // Email confirmation required
          this.showError('signupError', 'Please check your email to confirm your account.');
          submitBtn.textContent = 'Check your email';
        } else {
          this.hideModal();
        }
      }
    } catch (error) {
      this.showError('signupError', error.message || 'Sign up failed. Please try again.');
    } finally {
      const submitBtn = e.target.querySelector('.auth-submit');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Create Account';
      }
    }
  }

  /**
   * Handle forgot password form submission
   */
  async handleForgotPassword(e) {
    e.preventDefault();
    this.clearErrors();

    const email = document.getElementById('forgotEmail').value.trim();

    try {
      const submitBtn = e.target.querySelector('.auth-submit');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending...';

      await authService.resetPassword(email);

      const successEl = document.getElementById('forgotSuccess');
      successEl.textContent = 'Password reset link sent! Check your email.';
      successEl.style.display = 'block';
      submitBtn.textContent = 'Email sent';
    } catch (error) {
      this.showError('forgotError', error.message || 'Failed to send reset email.');
      const submitBtn = e.target.querySelector('.auth-submit');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Send Reset Link';
    }
  }

  /**
   * Handle password reset form submission (after clicking email link)
   */
  async handleResetPassword(e) {
    e.preventDefault();
    this.clearErrors();

    const password = document.getElementById('resetNewPassword').value;
    const confirmPassword = document.getElementById('resetConfirmPassword').value;

    if (password !== confirmPassword) {
      this.showError('resetPasswordError', 'Passwords do not match');
      return;
    }

    if (password.length < 6) {
      this.showError('resetPasswordError', 'Password must be at least 6 characters');
      return;
    }

    const submitBtn = e.target.querySelector('.auth-submit');
    try {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Updating...';

      await authService.updatePassword(password);

      // Show success message
      const successEl = document.getElementById('resetPasswordSuccess');
      successEl.textContent = 'Password updated successfully!';
      successEl.style.display = 'block';
      submitBtn.textContent = 'Password Updated';

      // Clear URL hash and close modal after brief delay
      setTimeout(() => {
        if (window.location.hash) {
          history.replaceState(null, '', window.location.pathname);
        }
        this.hideModal();
        this.updateAuthHeader(); // User is now logged in
      }, 1500);
    } catch (error) {
      this.showError('resetPasswordError', error.message || 'Failed to update password');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Update Password';
    }
  }

  /**
   * Handle OAuth sign in
   * If user is currently anonymous, links OAuth identity to preserve data
   */
  async handleOAuthSignIn(provider) {
    try {
      // If anonymous, link OAuth identity to preserve usage history
      // linkOAuthIdentity will fall back to regular signInWithOAuth if not anonymous
      if (authService.isAnonymous()) {
        await authService.linkOAuthIdentity(provider);
      } else {
        await authService.signInWithOAuth(provider);
      }
      // OAuth redirects, so the page will reload
    } catch (error) {
      console.error('OAuth error:', error);
      alert('OAuth sign in failed: ' + error.message);
    }
  }

  /**
   * Update the auth header based on current auth state
   * Anonymous users see logged-out UI (login/signup buttons) since they need to link identity
   */
  updateAuthHeader() {
    const loggedOut = document.getElementById('authLoggedOut');
    const loggedIn = document.getElementById('authLoggedIn');
    const userEmail = document.getElementById('headerUserEmail');
    const tierBadge = document.getElementById('headerTierBadge');

    if (!loggedOut || !loggedIn) return;

    // Show logged-in state only for authenticated non-anonymous users
    // Anonymous users are technically authenticated but should see signup prompts
    const isReallyAuthenticated = authService.isAuthenticated() && !authService.isAnonymous();

    if (isReallyAuthenticated) {
      loggedOut.style.display = 'none';
      loggedIn.style.display = 'flex';

      const user = authService.getUser();
      if (userEmail) {
        userEmail.textContent = user.email;
      }

      // Tier badge will be updated by entitlement service
      // For now, default to FREE
      if (tierBadge) {
        tierBadge.textContent = 'FREE';
        tierBadge.className = 'tier-badge tier-free';
      }
    } else {
      loggedOut.style.display = 'flex';
      loggedIn.style.display = 'none';
    }
  }

  /**
   * Update tier badge display
   * @param {string} tier - Tier ID
   * @param {Object} tierInfo - Optional tier info object with badgeLabel
   */
  updateTierBadge(tier, tierInfo = null) {
    const tierBadge = document.getElementById('headerTierBadge');
    if (!tierBadge) return;

    // Use tierInfo.badgeLabel if available, otherwise fall back to tier ID
    const badgeLabel = tierInfo?.badgeLabel || tier.toUpperCase();
    tierBadge.textContent = badgeLabel;
    tierBadge.className = `tier-badge tier-${tier}`;
  }

  /**
   * Update credit balance display
   */
  updateCreditBalance(balance) {
    const creditsContainer = document.getElementById('headerCredits');
    const creditsBalance = document.getElementById('headerCreditsBalance');

    if (!creditsContainer || !creditsBalance) return;

    if (balance > 0) {
      creditsBalance.textContent = balance;
      creditsContainer.style.display = 'inline-flex';
    } else {
      creditsContainer.style.display = 'none';
    }
  }

  /**
   * Update header with entitlements data
   */
  updateEntitlements(entitlements) {
    if (!entitlements) return;

    // Pass tierInfo to updateTierBadge if available
    this.updateTierBadge(entitlements.tier, entitlements.tierInfo);

    if (entitlements.credits) {
      this.updateCreditBalance(entitlements.credits.balance);
    }

    // In Free Mode, hide tier badge and subscription-related links in header
    const freeMode = entitlements.freeMode === true;
    const tierBadge = document.getElementById('headerTierBadge');
    if (tierBadge) tierBadge.style.display = freeMode ? 'none' : '';
    const upgradeLink = document.getElementById('headerUpgradeLink');
    if (upgradeLink) upgradeLink.style.display = freeMode ? 'none' : '';
    const historyLink = document.getElementById('headerHistoryLink');
    if (historyLink) historyLink.style.display = freeMode ? 'none' : '';
  }
}

// Export singleton instance
export const authUI = new AuthUI();
